import { useState } from "react";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const CONTACT_BG =
    "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?auto=format&fit=crop&w=1600&q=70";

export const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in all fields.");
            return;
        }
        setSubmitting(true);
        setTimeout(() => {
            setSubmitting(false);
            toast.success("Thank you! We will get back to you shortly.");
            setForm({ name: "", email: "", message: "" });
        }, 700);
    };

    return (
        <section id="contact" data-testid="contact-section" className="section-bg relative py-24 lg:py-32 bg-white">
            <div
                className="section-bg__media"
                style={{ backgroundImage: `url('${CONTACT_BG}')` }}
                aria-hidden="true"
            />
            <div className="section-bg__veil veil-contact" aria-hidden="true" />
            <div className="blob blob--green" style={{ width: 360, height: 360, top: -120, left: -120 }} aria-hidden="true" />
            <div className="blob blob--gold" style={{ width: 280, height: 280, bottom: -100, right: -100, animationDelay: "3.5s" }} aria-hidden="true" />

            <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 relative">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5 reveal">
                        <span className="section-rule eyebrow">Get in Touch</span>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] text-stone-900">
                            We&rsquo;d love to <em className="not-italic text-[var(--brand-green)]">hear from you</em>.
                        </h2>
                        <p className="mt-5 text-stone-600 leading-relaxed">
                            Have questions about admissions, the D.Pharm course, scholarships or hostel
                            facilities? Reach us through any of the channels below or fill the form —
                            our admissions team responds within 24 hours.
                        </p>

                        <div className="mt-9 space-y-5">
                            <div className="flex gap-4 items-start" data-testid="contact-address">
                                <span className="w-11 h-11 rounded-xl bg-[var(--brand-green)]/10 text-[var(--brand-green)] flex items-center justify-center flex-shrink-0">
                                    <MapPin size={20} />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-stone-500">Campus</div>
                                    <div className="text-stone-800 mt-1">
                                        Vill + P.O. Kanachi, P.S. Mallarpur,<br />
                                        Dist. Birbhum, West Bengal — 731216
                                    </div>
                                </div>
                            </div>

                            <a
                                href="tel:8637584851"
                                className="flex gap-4 items-start group"
                                data-testid="contact-phone"
                            >
                                <span className="w-11 h-11 rounded-xl bg-[var(--brand-blue)]/10 text-[var(--brand-blue)] flex items-center justify-center flex-shrink-0">
                                    <Phone size={20} />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-stone-500">Phone</div>
                                    <div className="text-stone-800 mt-1 group-hover:text-[var(--brand-blue)] transition-colors">
                                        8637584851 / 8509650147
                                    </div>
                                </div>
                            </a>

                            <a
                                href="mailto:prasadeducationaltrust2021@gmail.com"
                                className="flex gap-4 items-start group"
                                data-testid="contact-email"
                            >
                                <span className="w-11 h-11 rounded-xl bg-[var(--brand-yellow)]/15 text-[var(--brand-yellow-dark,#a47f00)] flex items-center justify-center flex-shrink-0" style={{ color: "#9c7a00" }}>
                                    <Mail size={20} />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-stone-500">Email</div>
                                    <div className="text-stone-800 mt-1 group-hover:text-[var(--brand-green)] transition-colors break-all">
                                        prasadeducationaltrust2021@gmail.com
                                    </div>
                                </div>
                            </a>
                        </div>

                        <div className="mt-9 rounded-2xl overflow-hidden border border-stone-200 shadow-sm">
                            <iframe
                                title="Campus location"
                                src="https://www.google.com/maps?q=Mallarpur,+Birbhum,+West+Bengal&output=embed"
                                width="100%"
                                height="220"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        className="lg:col-span-7 tactile-card p-8 md:p-10 reveal delay-2"
                        data-testid="contact-form"
                    >
                        <h3 className="font-display text-2xl md:text-3xl text-stone-900">Send us a message</h3>
                        <p className="text-stone-500 text-sm mt-2">All fields are required.</p>

                        <div className="mt-8 space-y-5">
                            <div>
                                <label htmlFor="name" className="text-xs uppercase tracking-[0.16em] text-stone-500">
                                    Full Name
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value={form.name}
                                    onChange={handleChange}
                                    data-testid="contact-input-name"
                                    className="mt-2 w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3.5 text-stone-900 focus:outline-none focus:ring-2 focus:ring-[var(--brand-green)]/30 focus:border-[var(--brand-green)] transition"
                                    placeholder="e.g. Anwesha Roy"
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="text-xs uppercase tracking-[0.16em] text-stone-500">
                                    Email Address
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    data-testid="contact-input-email"
                                    className="mt-2 w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3.5 text-stone-900 focus:outline-none focus:ring-2 focus:ring-[var(--brand-green)]/30 focus:border-[var(--brand-green)] transition"
                                    placeholder="you@example.com"
                                />
                            </div>
                            <div>
                                <label htmlFor="message" className="text-xs uppercase tracking-[0.16em] text-stone-500">
                                    Your Message
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={form.message}
                                    onChange={handleChange}
                                    data-testid="contact-input-message"
                                    rows={5}
                                    className="mt-2 w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3.5 text-stone-900 focus:outline-none focus:ring-2 focus:ring-[var(--brand-green)]/30 focus:border-[var(--brand-green)] transition resize-none"
                                    placeholder="Tell us about your query — admission, course details, scholarships..."
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit"
                                className="btn-primary w-full justify-center disabled:opacity-70"
                            >
                                {submitting ? "Sending..." : "Send Message"}
                                <Send size={16} />
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default Contact;
